const findElem             = <T extends HTMLElement>(selector: string, ctx: Document | T = document): T | HTMLElement | null => ctx.querySelector(selector);
const findElems            = <T extends HTMLElement>(selector: string, ctx: Document | T = document): NodeListOf<T> | NodeListOf<HTMLElement> | null => ctx.querySelectorAll(selector);
const arrayFrom            = <T>(pseudoArray: ArrayLike<T> | null): T[] | [] => pseudoArray ? Array.from(pseudoArray) : [];
const addClass             = <T extends HTMLElement>(domElement: T | null, className: string): void | null => domElement ? domElement.classList.add(className) : null;
const removeClass          = <T extends HTMLElement>(domElement: T | null, className: string): void | null => domElement ? domElement.classList.remove(className) : null;
const hasClass             = <T extends HTMLElement>(domElement: T | null, className: string): boolean => domElement ? domElement.classList.contains(className) : false
const showErrorInConsole   = (... data: any[]): void => console.error(data);
const isCurrentUri         = (uri: string): boolean => {
    const current_uri: string = location.pathname.replace(/^[\s\/]+/, '').replace(/[\s\/]+$/, '');
    const given_uri: string   = uri.split('?')[0].replace(/^[\s\/]+/, '').replace(/[\s\/]+$/, '');

    return current_uri === given_uri;
};
const redirectTo           = (uri: string): string => location.href = uri;
const getSessionStorage    = (key: string): string | null => sessionStorage.getItem(key);
const setSessionStorage    = (key: string, value: string): void => sessionStorage.setItem(key, value);
const removeSessionStorage = (key: string): void => sessionStorage.removeItem(key);
const getLocalStorage      = (key: string): string | null => localStorage.getItem(key);
const setLocalStorage      = (key: string, value: string): void => localStorage.setItem(key, value);
const removeLocalStorage   = (key: string): void => localStorage.removeItem(key);

/**
 * Check if browser supports specific image format.
 */
const checkImageFormatSupport = async (format: 'avif' | 'webp'): Promise<boolean> => {
    const testImages: Record<'avif' | 'webp', string> = {
        avif: 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=',
        webp: 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA='
    };

    return new Promise<boolean>((resolve): void => {
        const img: HTMLImageElement = new Image();

        img.onload  = (): void => resolve(true);
        img.onerror = (): void => resolve(false);

        img.src = testImages[format];
    });
};

/**
 * Get supported image formats by browser.
 */
const getSupportedImageFormats = async (): Promise<string[]> => {
    const CACHE_KEY: string         = 'supported_image_formats';
    const cachedData: string | null = getLocalStorage(CACHE_KEY);

    if (cachedData) {
        return JSON.parse(cachedData);
    }

    // Run checks if no valid cache
    const formats: string[] = [];

    if (await checkImageFormatSupport('avif')) {
        formats.push('avif');
    }

    if (await checkImageFormatSupport('webp')) {
        formats.push('webp');
    }

    setLocalStorage(CACHE_KEY, JSON.stringify(formats));

    return formats;
};

/**
 * In-memory cache for supported formats (session-level).
 */
let cachedFormats: string[] | null = null;

/**
 * Get cached supported image formats (memory cache + localStorage).
 */
const getCachedSupportedFormats = async (): Promise<string[]> => {
    if (cachedFormats === null) {
        cachedFormats = await getSupportedImageFormats();
    }

    return cachedFormats;
};

type FetchFuncOptions = Record<string | number, string | number>;

const fetchFunc = async (url: string, data: FetchFuncOptions | FormData = {}, method: string = 'POST'): Promise<any> => {
    type TypeHeaders = {
        "X-Requested-With": string;
        contentType?: string;
        "X-CSRF-TOKEN"?: string;
        "X-Supported-Image-Formats"?: string;
    };

    type TypeOptions = {
        method?: string,
        headers: TypeHeaders,
        body?: FormData | string
    };

    const supportedFormats: string[] = await getCachedSupportedFormats();
    const headers: TypeHeaders       = {
              'X-Requested-With':          'XMLHttpRequest',
              'X-CSRF-TOKEN':              (<HTMLMetaElement | null>findElem('meta[name="csrf-token"]'))?.content || '',
              'X-Supported-Image-Formats': supportedFormats.join(','), // "avif,webp" or "webp" or ""
          },
          contentType                = 'application/json;charset=utf-8';
    let options: TypeOptions         = {
        method,
        headers
    };

    if (method.toUpperCase() === 'GET') {
        options = { headers };
    } else if (!(data instanceof FormData)) {
        options.headers.contentType = contentType;
        options.body                = JSON.stringify(data);
    } else if (data instanceof FormData) {
        options.body = data;
    }

    const response: Response = await fetch(url, options);

    if (!response.ok || response.status >= 400) {
        throw new Error(`Network response was not ok: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
};

export {
    findElem, findElems, addClass, removeClass, hasClass,
    arrayFrom, showErrorInConsole, isCurrentUri, redirectTo,
    getSessionStorage, setSessionStorage, removeSessionStorage,
    getLocalStorage, removeLocalStorage, setLocalStorage, fetchFunc
};
